import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SEV = { high: "border-l-red-500", amber: "border-l-amber-500", low: "border-l-slate-300" };

export default function ApprovalsCenter() {
  const [tab, setTab] = useState("pending");
  const [d, setD] = useState(null);
  const load = (t = tab) => api.get(`/owner/approvals?status=${t}`).then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (id, decision) => {
    let reason = "";
    if (decision === "rejected") { reason = window.prompt("Reason for rejection?") || ""; }
    await api.post(`/owner/approvals/${id}/decide`, { decision, reason });
    toast.success(`Request ${decision}`);
    load(tab);
  };

  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#1a5fd0]" /></div>;

  return (
    <div data-testid="approvals-center">
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Approvals Center</h1>
      <p className="mt-1 text-[13px] text-slate-500">One decision queue for provider activations, contracts, attestations and high-value actions. Every decision is audit-logged.</p>

      <div className="mt-5 flex gap-5 border-b border-slate-200">
        {["pending", "approved", "rejected"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`approvals-tab-${t}`}
            className={`pb-2.5 text-[14px] font-semibold capitalize transition-colors ${tab === t ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400 hover:text-slate-600"}`}>
            {t} <span className="ml-1 font-mono text-[11px] text-slate-400">{d.counts[t] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3" data-testid="approvals-list">
        {d.approvals.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white py-12 text-center">
            <ShieldQuestion size={22} className="text-slate-300" />
            <p className="text-[13px] text-slate-400">No {tab} approvals.</p>
          </div>
        ) : d.approvals.map((a) => (
          <div key={a.id} className={`card-spotlight rounded-2xl border border-l-4 border-slate-200 bg-white p-4 ${SEV[a.severity] || SEV.low}`} data-testid={`approval-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-head text-[14px] font-bold text-slate-900">{a.title}</div>
                <p className="mt-0.5 text-[13px] text-slate-500">{a.detail}</p>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  {a.type} · requested by {a.requested_by || "system"}{a.reason ? ` · reason: ${a.reason}` : ""}
                </div>
              </div>
              {a.status === "pending" && (
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => decide(a.id, "approved")} data-testid={`approve-${a.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 font-head text-[12px] font-bold text-white hover:bg-emerald-700"><CheckCircle2 size={13} /> Approve</button>
                  <button onClick={() => decide(a.id, "rejected")} data-testid={`reject-${a.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 font-head text-[12px] font-bold text-slate-700 hover:bg-slate-50"><XCircle size={13} /> Reject</button>
                </div>
              )}
              {a.status !== "pending" && (
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase ${a.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{a.status}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
